import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { FunctionalityEnum } from '@elearning-platform/api-types/enums';

import { AvatarType } from "../../components/avatar/avatar.constants";
import { Avatar } from "../../components/avatar/avatar.component";
import { Loader } from "../../components/loader/loader.component";
import { renderWhenTrue } from "../../helpers/rendering";
import { useModuleConfig, ModuleType } from "../";
import { useAuth } from '../../hooks';

import { ProfileActions } from "./redux/profile.reducer";
import {
  selectIsPending,
  selectAvatarSrc,
  selectFullName,
  selectPosition,
} from "./redux/profile.selectors";
import { ProfileTabType } from "./profile.constants";
import {
  Container,
  HeaderWrapper,
  Header,
  TextWrapper,
  Name,
  Position,
  Tabs,
  TabText,
  Tab,
} from "./profile.styled";
import { CheckAuthLayout } from '../../components';
import { NotFoundPage } from '../../app/notFoundPage/notFoundPage.component';

import { profileRoutes } from './profile.routes';

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const config = useModuleConfig(ModuleType.Profile);
  const dispatch = useDispatch();
  const isPending = useSelector(selectIsPending);
  const avatarSrc = useSelector(selectAvatarSrc);
  const name = useSelector(selectFullName);
  const position = useSelector(selectPosition);
  const [activeTab, setActiveTab] = useState(ProfileTabType.Details);
  const { hasUserRequiredFunctionalities, isAuthInitialized } = useAuth();

  const profileTabs = useMemo(
    () => ({
      ...hasUserRequiredFunctionalities([
        FunctionalityEnum.ApiProfileRead, FunctionalityEnum.ApiUserExperiences
      ])
        ? { [ProfileTabType.Details]: true } : {},
      ...hasUserRequiredFunctionalities([
        FunctionalityEnum.ApiProfileRead,
        FunctionalityEnum.ApiProfileReadForm,
        FunctionalityEnum.ApiProfileUpdate,
        FunctionalityEnum.ApiBrands
      ])
        ? { [ProfileTabType.Edit]: true } : {},
      ...hasUserRequiredFunctionalities([
        FunctionalityEnum.ApiProfileRead, FunctionalityEnum.ApiAgreementUpdate, FunctionalityEnum.ApiAgreementList
      ])
        ? { [ProfileTabType.Agreements]: true } : {},
      ...{ [ProfileTabType.Cookies]: true },
      ...hasUserRequiredFunctionalities([FunctionalityEnum.ApiUserUpdatePassword])
        ? { [ProfileTabType.Passwords]: true } : {},
      ...hasUserRequiredFunctionalities([FunctionalityEnum.ApiProfileRead, FunctionalityEnum.ApiProfileFacsimileDownload])
        ? { [ProfileTabType.Facsimile]: true } : {},
    }),
    [isAuthInitialized]
  );

  useEffect(() => {
    dispatch(ProfileActions.fetchHeaderInfo());
  }, [dispatch]);

  useEffect(() => {
    if (config.urls.includes(location.pathname) && Object.keys(profileTabs).length) {
      navigate(Object.keys(profileTabs)[0], { replace: true });
    }
  }, [navigate, location, profileTabs]);

  useEffect(() => {
    const tabFromUrl = location.pathname.replace(`${config.urls[1]}/`, "");

    if (Object.values(ProfileTabType).includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    } else {
      setActiveTab(ProfileTabType.Details);
    }
  }, [location.pathname]);

  const renderTabButton = (tab) => (
    <Tab onClick={() => setActiveTab(tab)} to={tab}>
      <TabText $active={activeTab === tab}>
        {t(`profile.tabs.${tab}`)}
      </TabText>
    </Tab>
  );

  const renderTabContent = () => (
    <Routes>
      <Route element={<CheckAuthLayout />}>
        {profileRoutes.map((route) =>
          Object.values(route.pathSettings.urls).map((url, index) => {
            const Component = route.component;

            return (
              <Route
                key={`${url}-${index}`}
                path={url}
                element={<Component />}
              />
            );
          })
        )}
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );

  const renderLoader = renderWhenTrue(() => <Loader />);

  const renderContent = renderWhenTrue(() => (
    <>
      <HeaderWrapper>
        <Header>
          <Avatar src={avatarSrc} name={name} type={AvatarType.ExtraLarge} />
          <TextWrapper>
            <Name>{name}</Name>
            <Position>{position}</Position>
          </TextWrapper>
        </Header>
        <Tabs>
          {Object.keys(profileTabs).map((profileTab) => renderTabButton(profileTab))}
        </Tabs>
      </HeaderWrapper>
      {renderTabContent()}
    </>
  ));

  return (
    <ThemeProvider theme={config.theme}>
      <Container>
        {renderContent(!isPending)}
        {renderLoader(isPending)}
      </Container>
    </ThemeProvider>
  );
};

export default Profile;
